import React from "react";
import { Box, Typography, Stack, useTheme, useMediaQuery, keyframes } from "@mui/material";
import { SpaceBetweenBox } from "../../../commons/BoxAbstraction";

// Gentle float animation for icons
const gentleFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

// Pulse animation for step numbers
const stepPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(53,103,214,0.3); }
  50% { box-shadow: 0 0 0 8px rgba(53,103,214,0); }
`;

const OnboardingFlowSection = () => {

  // Onboarding steps data matching the landing page structure
  const onboardingSteps = [
    {
      image: "mapping.svg",
      description: "Parameter Mapping & Product Configuration",
    },
    {
      image: "productConfig.svg",
      description: "API integration with LIS, HIS & EHR",
    },
    {
      image: "AI.svg",
      description:
        "After go-live, our engine instantly processes and returns the output",
    },
    {
      image: "reportFormat.svg",
      description: "Patients get Smart Reports through preferred mediums",
    },
    {
      image: "patientEngagement.svg",
      description:
        "Turn lab visits into ongoing patient relationships with smart followups and engagement tools",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "40px 16px",
          sm: "50px 24px",
          md: "60px 64px",
        },
        backgroundColor: "transparent",
      }}
    >
      {/* Header with enhanced styling */}
      <Box sx={{ textAlign: "center", marginBottom: { xs: "32px", md: "48px" } }}>
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "26px", sm: "32px", md: "40px" },
            fontWeight: 600,
            color: "#0f172a",
            marginBottom: "12px",
            letterSpacing: "-0.5px",
          }}
        >
          Seamless{" "}
          <Box 
            component="span" 
            sx={{ 
              color: "#3567D6",
              position: "relative",
            }}
          >
            Onboarding
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: { xs: "14px", md: "17px" },
            fontWeight: 400,
            color: "#64748b",
            maxWidth: "400px",
            margin: "0 auto",
          }}
        >
          From Setup to Smart Reporting in 5 Simple Steps
        </Typography>
      </Box>

      {/* Flow Container */}
      <SpaceBetweenBox
        sx={{
          height: { xs: "auto", sm: "280px", md: "450px" },
          minHeight: { xs: "400px", sm: "280px" },
          position: "relative",
          width: "100%",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "center" },
          gap: { xs: "30px", sm: "0" },
        }}
      >
        {onboardingSteps.map((each, index, array) => (
          <Stack
            key={index}
            direction="column"
            alignItems="center"
            sx={{
              width: { xs: "80%", sm: "20%" },
              maxWidth: { xs: "250px", sm: "none" },
              alignSelf: { 
                xs: "center", 
                sm: index % 2 === 0 ? "flex-end" : "flex-start" 
              },
              position: "relative",
            }}
          >
            {/* Up Arrow - only for even indices (except last) on desktop */}
            {index < array.length - 1 && index % 2 === 0 && (
              <Box
                component="img"
                src="/images/upArrow.png"
                alt="upArrow"
                sx={{
                  display: { xs: "none", sm: "block" },
                  margin: {
                    sm: "0px 0px 15px 40px",
                    md: "0px 0px 45px 85px",
                  },
                  width: { sm: "50%", md: "40%" },
                }}
              />
            )}

            {/* Step Number Badge */}
            <Box
              sx={{
                width: { xs: "32px", sm: "28px", md: "36px" },
                height: { xs: "32px", sm: "28px", md: "36px" },
                borderRadius: "50%",
                background: "linear-gradient(135deg, #3567D6 0%, #5B8DEF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: { xs: "12px", sm: "8px", md: "14px" },
                boxShadow: "0 4px 14px rgba(53,103,214,0.3)",
                animation: `${stepPulse} 2s ease-in-out infinite`,
                animationDelay: `${index * 0.3}s`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "14px", sm: "12px", md: "15px" },
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                {index + 1}
              </Typography>
            </Box>

            {/* Image with hover animation */}
            <Box
              sx={{
                position: "relative",
                padding: { xs: "8px", sm: "6px", md: "12px" },
                backgroundColor: "rgba(255,255,255,0.8)",
                borderRadius: "16px",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(53,103,214,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(53,103,214,0.12)",
                  "& img": {
                    animation: `${gentleFloat} 2s ease-in-out infinite`,
                  },
                },
              }}
            >
              <Box
                component="img"
                src={`/images/${each.image}`}
                alt={each.description}
                sx={{
                  width: { xs: "100px", sm: "70px", md: "140px" },
                  height: { xs: "80px", sm: "70px", md: "130px" },
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "13px", sm: "11px", md: "13px" },
                fontWeight: 500,
                marginTop: { xs: "12px", sm: "10px", md: "16px" },
                textAlign: "center",
                alignSelf: "stretch",
                color: "#475569",
                lineHeight: { xs: "18px", sm: "15px", md: "19px" },
                padding: { xs: "0 8px", sm: "0 4px", md: "0" },
              }}
            >
              {each.description}
            </Typography>

            {/* Down Arrow - only for odd indices on desktop */}
            {index % 2 !== 0 && (
              <Box
                component="img"
                src="/images/downArrow.png"
                alt="downArrow"
                sx={{
                  display: { xs: "none", sm: "block" },
                  margin: {
                    sm: "15px 0px 0px 40px",
                    md: "45px 0px 0px 85px",
                  },
                  width: { sm: "50%", md: "40%" },
                }}
              />
            )}

            {/* Mobile Arrow - connecting to next item */}
            {index < array.length - 1 && (
              <Box
                sx={{
                  display: { xs: "flex", sm: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "16px",
                  gap: "4px",
                }}
              >
                <Box
                  sx={{
                    width: "2px",
                    height: "24px",
                    background: "linear-gradient(180deg, #3567D6 0%, rgba(53,103,214,0.3) 100%)",
                    borderRadius: "1px",
                  }}
                />
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderTop: "8px solid rgba(53,103,214,0.5)",
                  }}
                />
              </Box>
            )}
          </Stack>
        ))}
      </SpaceBetweenBox>
    </Box>
  );
};

export default OnboardingFlowSection;
